import { Fundamental } from "pages/analyze-stock";
import GaugeChart from "src/common/charts/gaugeChart";
import { useEffect, useState, useMemo } from "react";
import { fetchWrapper } from "src/helpers";
import StockFundamentalRatioSkeleton from "src/components/skeleton/StockFundamentalRatioSkeleton";
import { getLocalStorageUser } from "src/helpers/api/sharedFunction";

interface StockFundamentalRatioProps {
  selectedCompany: string;
  darkMode: boolean | undefined;
}

const StockFundamentalRatioModal = ({
  selectedCompany,
  darkMode,
}: StockFundamentalRatioProps) => {
  const user = getLocalStorageUser() || {};
  const token = user?.token;
  const [fundamentals, setFundamentals] = useState<Fundamental[]>([]);
  const [commentary, setCommentary] = useState<string>("");
  const [score, setScore] = useState(0);
  const [loading, setLoading] = useState(false);

  const getFundamentals = async (symbol: string) => {
    try {
      setLoading(true);
      const res = await fetchWrapper.get(
        "/api/dashboard/fundamentals?symbol=" + encodeURIComponent(symbol),
        undefined,
        { Authorization: `Bearer ${token}` },
      );
      setFundamentals(Array.isArray(res.data) ? res.data : []);
      const result = await fetchWrapper.get(
        "/api/dashboard/fundamental_analysis?symbol=" +
          encodeURIComponent(symbol),
        undefined,
        { Authorization: `Bearer ${token}` },
      );
      setCommentary(result.commentry);
      setScore(result.score);
    } catch (_error) {
      setFundamentals([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getFundamentals(selectedCompany);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany]);

  const fundamentalsMap = useMemo(
    () =>
      fundamentals.reduce(
        (acc, f) => ({ ...acc, [f.metric]: f.value }),
        {} as Record<string, string>,
      ),
    [fundamentals],
  );

  const SECTIONS = [
    {
      title: "Fundamental Ratios",
      keys: [
        "Price to Earning",
        "Industry PE",
        "PEG Ratio",
        "Price to book value",
        "Industry PBV",
      ],
    },
    {
      title: "Profitability",
      keys: ["Return on equity", "ROCE", "OPM", "NPM last year"],
    },
    {
      title: "Risk & Solvency",
      keys: [
        "Altman Z Score",
        "Debt to equity",
        "Int Coverage",
        "High price",
        "Low price",
      ],
    },
    {
      title: "Ownership / Sentiment",
      keys: [
        "Promoter holding",
        "Change in Prom Hold",
        "Piotroski score",
        "FII holding",
        "DII holding",
      ],
    },
    {
      title: "Other Financials",
      keys: ["EVEBITDA", "Earnings yield", "CMP / FCF"],
    },
  ];

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-transparent">
      {loading ? (
        <div className="min-h-0 flex-1 overflow-y-auto custom-scroll">
          <StockFundamentalRatioSkeleton isTabbedView={true} />
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto custom-scroll">
          <div className="grid min-h-[420px] grid-cols-1 gap-2 space-y-6 px-3 py-2 md:grid-cols-3">
            {SECTIONS.map((section) => (
              <div key={section.title} className="space-y-2">
                <h3 className="mb-1 text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-400">
                  {section.title}
                </h3>
                <div
                  className={`grid gap-0 overflow-hidden rounded-md border border-gray-200 dark:border-gray-700 ${
                    section.keys.length > 3
                      ? "grid-cols-1 md:grid-cols-1 sm:h-full"
                      : "!flex flex-col gap-1.5 sm:h-full"
                  }`}
                >
                  {section.keys.map((key, i) => (
                    <div
                      key={key}
                      className={`flex items-center justify-between px-4 py-2 ${
                        i !== section.keys.length - 1
                          ? "border-b border-gray-200 dark:border-gray-700 md:border-b-0"
                          : ""
                      }`}
                    >
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {key}
                      </span>
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {fundamentalsMap[key] ?? "—"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="flex flex-col items-start justify-start">
              <p className="mb-1 text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-400">
                Overall Score
              </p>
              <div className="relative flex h-full w-full items-center justify-center">
                <GaugeChart darkMode={darkMode} value={score} />
              </div>
            </div>
          </div>
          <div className="mt-3 border-t border-gray-200 px-6 py-4 text-sm font-medium text-justify text-gray-500 dark:border-gray-700 dark:text-gray-400">
            <p>{commentary}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockFundamentalRatioModal;
